import { supabase } from '../lib/supabase';

export interface UserProfile {
  id: string;
  fullName: string;
  email: string;
  age: number | null;
  nationality: string | null;
  gender: string | null;
  totalSavings: number;
  savingsPerUser: number;
  avgRedemptionAmount: number;
  conversionRate: number;
  redemptionsCount: number;
  createdAt: string;
  isActivated: boolean;
}

export interface UserManagementStats {
  totalUsers: number;
  avgAge: number;
  avgSavingsPerUser: number;
  avgConversionRate: number;
  totalSavings: number;
}

export interface NationalityDistribution {
  nationality: string;
  count: number;
  percentage: number;
}

export interface GenderDistribution {
  gender: string;
  count: number;
  percentage: number;
}

export interface PaginatedUsersResponse {
  users: UserProfile[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
  hasPreviousPage: boolean;
}

export type SortField = 'fullName' | 'age' | 'nationality' | 'gender' | 'totalSavings' | 'avgRedemptionAmount' | 'conversionRate' | 'createdAt';
export type SortDirection = 'asc' | 'desc';

class UserManagementService {
  async getUsersWithStatsPaginated(
    page: number = 1, 
    limit: number = 25, 
    searchTerm?: string,
    nationalityFilter?: string,
    genderFilter?: string,
    ageRange?: { min: number; max: number },
    sortField?: SortField,
    sortDirection?: SortDirection
  ): Promise<PaginatedUsersResponse> {
    try {
      const offset = (page - 1) * limit;
      
      // Build query with filters
      let query = supabase
        .from('user_profiles')
        .select(`
          id,
          full_name,
          email,
          date_of_birth,
          nationality,
          gender,
          created_at,
          is_activated
        `, { count: 'exact' });

      // Apply search filter
      if (searchTerm) {
        query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      // Apply nationality filter
      if (nationalityFilter && nationalityFilter !== 'all') {
        query = query.eq('nationality', nationalityFilter);
      }

      // Apply gender filter
      if (genderFilter && genderFilter !== 'all') {
        query = query.eq('gender', genderFilter);
      }

      // Apply server-side sorting based on sortField
      if (sortField && sortDirection) {
        const ascending = sortDirection === 'asc';
        
        switch (sortField) {
          case 'fullName':
            query = query.order('full_name', { ascending });
            break;
          case 'age':
            query = query.order('date_of_birth', { ascending: !ascending }); // Reverse for age (older = desc date)
            break;
          case 'nationality':
            query = query.order('nationality', { ascending, nullsFirst: false });
            break;
          case 'gender':
            query = query.order('gender', { ascending, nullsFirst: false });
            break;
          case 'createdAt':
            query = query.order('created_at', { ascending });
            break;
          default:
            // For calculated fields (totalSavings, avgRedemptionAmount, conversionRate), 
            // we'll sort after fetching since they require redemption data
            query = query.order('created_at', { ascending: false });
            break;
        }
      } else {
        // Default sorting
        query = query.order('created_at', { ascending: false });
      }

      // Apply pagination
      query = query.range(offset, offset + limit - 1);

      const { data: users, error, count } = await query;

      if (error) throw error;

      // Calculate stats for each user
      const usersWithStats = await Promise.all(
        (users || []).map(async (user) => {
          const stats = await this.getUserStats(user.id);
          
          // Calculate age from date_of_birth
          const age = user.date_of_birth 
            ? Math.floor((new Date().getTime() - new Date(user.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
            : null;

          // Apply age filter if specified
          if (ageRange && age !== null) {
            if (age < ageRange.min || age > ageRange.max) {
              return null;
            }
          }

          return {
            id: user.id,
            fullName: user.full_name || 'Unknown',
            email: user.email,
            age,
            nationality: user.nationality,
            gender: user.gender,
            totalSavings: stats.totalSavings,
            savingsPerUser: stats.totalSavings,
            avgRedemptionAmount: stats.avgRedemptionAmount,
            conversionRate: stats.conversionRate,
            redemptionsCount: stats.redemptionsCount,
            createdAt: user.created_at,
            isActivated: user.is_activated || false
          };
        })
      );

      // Filter out null values (users that didn't match age filter)
      let filteredUsers = usersWithStats.filter(user => user !== null) as UserProfile[];

      // Apply client-side sorting for calculated fields (totalSavings, avgRedemptionAmount, conversionRate)
      if (sortField && sortDirection && ['totalSavings', 'avgRedemptionAmount', 'conversionRate'].includes(sortField)) {
        filteredUsers = filteredUsers.sort((a, b) => {
          const aValue = a[sortField];
          const bValue = b[sortField];
          
          if (aValue === null || aValue === undefined) return 1;
          if (bValue === null || bValue === undefined) return -1;
          
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
          }
          
          return 0;
        });
      }
      
      const totalCount = count || 0;
      const totalPages = Math.ceil(totalCount / limit);

      return {
        users: filteredUsers,
        totalCount,
        currentPage: page,
        totalPages,
        hasNextPage: page < totalPages,
        hasPreviousPage: page > 1
      };
    } catch (error) {
      console.error('Error fetching paginated users:', error);
      throw error;
    }
  }

  // New method to get all users with server-side sorting (for complete dataset sorting)
  async getAllUsersSorted(
    sortField: SortField,
    sortDirection: SortDirection,
    searchTerm?: string,
    nationalityFilter?: string,
    genderFilter?: string,
    ageRange?: { min: number; max: number }
  ): Promise<UserProfile[]> {
    try {
      // Build query with filters
      let query = supabase
        .from('user_profiles')
        .select(`
          id,
          full_name,
          email,
          date_of_birth,
          nationality,
          gender,
          created_at,
          is_activated
        `);

      // Apply search filter
      if (searchTerm) {
        query = query.or(`full_name.ilike.%${searchTerm}%,email.ilike.%${searchTerm}%`);
      }

      // Apply nationality filter
      if (nationalityFilter && nationalityFilter !== 'all') {
        query = query.eq('nationality', nationalityFilter);
      }

      // Apply gender filter
      if (genderFilter && genderFilter !== 'all') {
        query = query.eq('gender', genderFilter);
      }

      // Apply server-side sorting based on sortField
      const ascending = sortDirection === 'asc';
      
      switch (sortField) {
        case 'fullName':
          query = query.order('full_name', { ascending });
          break;
        case 'age':
          query = query.order('date_of_birth', { ascending: !ascending }); // Reverse for age (older = desc date)
          break;
        case 'nationality':
          query = query.order('nationality', { ascending, nullsFirst: false });
          break;
        case 'gender':
          query = query.order('gender', { ascending, nullsFirst: false });
          break;
        case 'createdAt':
          query = query.order('created_at', { ascending });
          break;
        default:
          // For calculated fields, we'll sort after fetching
          query = query.order('created_at', { ascending: false });
          break;
      }

      const { data: users, error } = await query;

      if (error) throw error;

      // Calculate stats for each user
      const usersWithStats = await Promise.all(
        (users || []).map(async (user) => {
          const stats = await this.getUserStats(user.id);
          
          // Calculate age from date_of_birth
          const age = user.date_of_birth 
            ? Math.floor((new Date().getTime() - new Date(user.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
            : null;

          // Apply age filter if specified
          if (ageRange && age !== null) {
            if (age < ageRange.min || age > ageRange.max) {
              return null;
            }
          }

          return {
            id: user.id,
            fullName: user.full_name || 'Unknown',
            email: user.email,
            age,
            nationality: user.nationality,
            gender: user.gender,
            totalSavings: stats.totalSavings,
            savingsPerUser: stats.totalSavings,
            avgRedemptionAmount: stats.avgRedemptionAmount,
            conversionRate: stats.conversionRate,
            redemptionsCount: stats.redemptionsCount,
            createdAt: user.created_at,
            isActivated: user.is_activated || false
          };
        })
      );

      // Filter out null values (users that didn't match age filter)
      let filteredUsers = usersWithStats.filter(user => user !== null) as UserProfile[];

      // Apply client-side sorting for calculated fields (totalSavings, avgRedemptionAmount, conversionRate)
      if (['totalSavings', 'avgRedemptionAmount', 'conversionRate'].includes(sortField)) {
        filteredUsers = filteredUsers.sort((a, b) => {
          const aValue = a[sortField];
          const bValue = b[sortField];
          
          if (aValue === null || aValue === undefined) return 1;
          if (bValue === null || bValue === undefined) return -1;
          
          if (typeof aValue === 'number' && typeof bValue === 'number') {
            return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
          }
          
          return 0;
        });
      }

      return filteredUsers;
    } catch (error) {
      console.error('Error fetching all users sorted:', error);
      throw error;
    }
  }

  async getAllUsersWithStats(): Promise<UserProfile[]> {
    try {
      const { data: users, error } = await supabase
        .from('user_profiles')
        .select(`
          id,
          full_name,
          email,
          date_of_birth,
          nationality,
          gender,
          created_at,
          is_activated
        `)
        .neq('role', 'partner') // Exclude users with partner status
        .order('created_at', { ascending: false });

      if (error) throw error;

      // Calculate stats for each user
      const usersWithStats = await Promise.all(
        users.map(async (user) => {
          const stats = await this.getUserStats(user.id);
          
          // Calculate age from date_of_birth
          const age = user.date_of_birth 
            ? Math.floor((new Date().getTime() - new Date(user.date_of_birth).getTime()) / (365.25 * 24 * 60 * 60 * 1000))
            : null;

          return {
            id: user.id,
            fullName: user.full_name || 'Unknown',
            email: user.email,
            age,
            nationality: user.nationality,
            gender: user.gender,
            totalSavings: stats.totalSavings,
            savingsPerUser: stats.totalSavings,
            avgRedemptionAmount: stats.avgRedemptionAmount,
            conversionRate: stats.conversionRate,
            redemptionsCount: stats.redemptionsCount,
            createdAt: user.created_at,
            isActivated: user.is_activated || false
          };
        })
      );

      return usersWithStats;
    } catch (error) {
      console.error('Error fetching users with stats:', error);
      throw error;
    }
  }

  async getUserStats(userId: string) {
    try {
      // Get user redemptions for calculations
      const { data: redemptions, error } = await supabase
        .from('redemptions')
        .select('saved_amount, final_price, redemption_date')
        .eq('user_id', userId);

      if (error) throw error;

      const redemptionsCount = redemptions?.length || 0;
      const totalSavings = redemptions?.reduce((sum, r) => sum + (Number(r.saved_amount) || 0), 0) || 0;
      const avgRedemptionAmount = redemptionsCount > 0 
        ? redemptions.reduce((sum, r) => sum + (Number(r.final_price) || 0), 0) / redemptionsCount 
        : 0;

      // Conversion rate calculation (registration to first redemption)
      // For this demo, we'll use redemption count > 0 as conversion indicator
      const conversionRate = redemptionsCount > 0 ? 100 : 0;

      return {
        totalSavings,
        avgRedemptionAmount,
        conversionRate,
        redemptionsCount
      };
    } catch (error) {
      console.error('Error calculating user stats:', error);
      return {
        totalSavings: 0,
        avgRedemptionAmount: 0,
        conversionRate: 0,
        redemptionsCount: 0
      };
    }
  }

  async getUserManagementStats(): Promise<UserManagementStats> {
    try {
      // Get total users count excluding partners
      const { count: totalUsers, error: usersError } = await supabase
        .from('user_profiles')
        .select('*', { count: 'exact', head: true })
        .neq('role', 'partner'); // Exclude users with partner status

      if (usersError) throw usersError;

      // Get all users for calculations
      const users = await this.getAllUsersWithStats();

      const avgAge = users.filter(u => u.age !== null).reduce((sum, u) => sum + (u.age || 0), 0) / 
                    users.filter(u => u.age !== null).length || 0;

      const totalSavings = users.reduce((sum, u) => sum + u.totalSavings, 0);
      const avgSavingsPerUser = users.length > 0 ? totalSavings / users.length : 0;
      
      const avgConversionRate = users.length > 0 
        ? users.reduce((sum, u) => sum + u.conversionRate, 0) / users.length 
        : 0;

      return {
        totalUsers: totalUsers || 0,
        avgAge: Math.round(avgAge),
        avgSavingsPerUser: Math.round(avgSavingsPerUser * 100) / 100,
        avgConversionRate: Math.round(avgConversionRate * 100) / 100,
        totalSavings: Math.round(totalSavings * 100) / 100
      };
    } catch (error) {
      console.error('Error fetching user management stats:', error);
      throw error;
    }
  }

  async getNationalityDistribution(): Promise<NationalityDistribution[]> {
    try {
      const { data, error } = await supabase.rpc('get_nationality_distribution');
      
      if (error) {
        console.error('Error fetching nationality distribution:', error);
        throw error;
      }

      return data.map((item: any) => ({
        nationality: item.nationality_name,
        count: item.count_value,
        percentage: parseFloat(item.percentage)
      }));
    } catch (error) {
      console.error('Error in getNationalityDistribution:', error);
      throw error;
    }
  }

  async getGenderDistribution(): Promise<GenderDistribution[]> {
    try {
      const { data, error } = await supabase.rpc('get_gender_distribution');
      
      if (error) {
        console.error('Error fetching gender distribution:', error);
        throw error;
      }

      return data.map((item: any) => ({
        gender: item.gender_label,
        count: item.count_value,
        percentage: parseFloat(item.percentage)
      }));
    } catch (error) {
      console.error('Error in getGenderDistribution:', error);
      throw error;
    }
  }

  async deleteUser(userId: string): Promise<void> {
    try {
      // Delete user from Supabase (cascading deletes should handle related records)
      const { error } = await supabase
        .from('user_profiles')
        .delete()
        .eq('id', userId);

      if (error) throw error;
    } catch (error) {
      console.error('Error deleting user:', error);
      throw error;
    }
  }

  async activateUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const { data, error } = await supabase.rpc('admin_activate_user', {
        target_user_id: userId
      });

      if (error) {
        console.error('Error activating user:', error);
        throw error;
      }

      if (data && data.length > 0) {
        const result = data[0];
        return {
          success: result.success,
          message: result.message
        };
      }

      return {
        success: false,
        message: 'Unexpected response from activation function'
      };
    } catch (error: any) {
      console.error('Error in activateUser:', error);
      return {
        success: false,
        message: error.message || 'Failed to activate user'
      };
    }
  }

  async deactivateUser(userId: string): Promise<{ success: boolean; message: string }> {
    try {
      const { data, error } = await supabase.rpc('admin_deactivate_user', {
        target_user_id: userId
      });

      if (error) {
        console.error('Error deactivating user:', error);
        throw error;
      }

      if (data && data.length > 0) {
        const result = data[0];
        return {
          success: result.success,
          message: result.message
        };
      }

      return {
        success: false,
        message: 'Unexpected response from deactivation function'
      };
    } catch (error: any) {
      console.error('Error in deactivateUser:', error);
      return {
        success: false,
        message: error.message || 'Failed to deactivate user'
      };
    }
  }
}

export const userManagementService = new UserManagementService();